/** Família: o caminho que faltava para a visão conjunta do casal (lacuna L16 do mapa).
 *
 * O seletor Meu / Família na barra do alto só aparece quando existe família com mais de um
 * membro — e até aqui não havia lugar nenhum no app para criar uma, então a decisão central
 * do produto (ver o dinheiro dos dois numa projeção só) estava inalcançável pela interface.
 * As quatro funções de `api.ts` existiam desde a F1 e nunca foram chamadas.
 *
 * Compartilhar é conta por conta e por vontade do dono: a família não abre a carteira inteira
 * de ninguém. O que não entra em `account_shares` continua invisível para o outro membro.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Empty, ErrorBanner, Face, Field, FieldGroup, Key, Label, Skeleton } from "../components/ui";
import { money } from "../lib/format";
import {
  keys,
  useAccounts,
  useAddMember,
  useCreateFamily,
  useFamilies,
  useShareAccount,
  useUnshareAccount,
} from "../lib/queries";
import { useSession } from "../lib/session";
import * as api from "../lib/api";
import type { Account, Family } from "../lib/types";

/** A tecla do sistema mede 32px; no dedo o alvo mínimo é 44. */
const ALVO = { minHeight: 44 };

type Visibilidade = "full" | "balance_only";
type Estado = "none" | Visibilidade;

const ESTADOS: { id: Estado; label: string }[] = [
  { id: "none", label: "Não entra" },
  { id: "full", label: "Tudo" },
  { id: "balance_only", label: "Só o saldo" },
];

const rotulo = (e: Estado) => ESTADOS.find((x) => x.id === e)?.label ?? "—";

/** Duas letras para o mostrador da linha, no lugar onde as outras telas põem o dia. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

export function Familia() {
  const nav = useNavigate();
  const familias = useFamilies();
  const contas = useAccounts();

  const cabeca = (
    <div className="monthbar">
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Key icon="chevronLeft" title="Voltar" onClick={() => nav("/ajustes")} style={ALVO} />
        <h2>Família</h2>
      </div>
    </div>
  );

  if (familias.isPending || contas.isPending)
    return (
      <section className="view on" aria-label="Família">
        <Skeleton lines={6} />
      </section>
    );

  if (familias.error || contas.error)
    return (
      <section className="view on" aria-label="Família">
        <div className="plan">
          {cabeca}
          <ErrorBanner
            message={((familias.error ?? contas.error) as Error).message}
            onRetry={() => {
              familias.refetch();
              contas.refetch();
            }}
          />
        </div>
      </section>
    );

  // `useFamilies` já tira a "família zumbi": a que o usuário criou e depois deixou, que a
  // RLS continua devolvendo e a tela mostrava com 0 pessoas e nenhuma ação possível.
  const lista = familias.data ?? [];

  return (
    <section className="view on" aria-label="Família">
      <div className="plan">
        {cabeca}

        <Face className="meter">
          <Label>Para que serve</Label>
          <div className="sub">
            A família junta, numa projeção só, as contas que cada um escolhe compartilhar. O que
            fica de fora continua só seu. É o que liga o seletor <b>Meu / Família</b> na barra do
            alto — ele aparece quando a família tem mais de uma pessoa.
          </div>
        </Face>

        {lista.length === 0 && (
          <Empty title="Nenhuma família ainda">
            Crie uma, adicione quem divide as contas com você e diga quais contas entram.
          </Empty>
        )}

        {lista.map((f) => (
          <BlocoFamilia key={f.id} familia={f} contas={contas.data ?? []} />
        ))}

        <NovaFamilia primeira={lista.length === 0} />
      </div>
    </section>
  );
}

function NovaFamilia({ primeira }: { primeira: boolean }) {
  const criar = useCreateFamily();
  const [aberto, setAberto] = useState(primeira);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const enviar = async () => {
    setErro(null);
    try {
      await criar.mutateAsync(nome.trim());
      setNome("");
      setAberto(primeira);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  if (!aberto)
    return (
      <div>
        <Key icon="users" style={ALVO} onClick={() => setAberto(true)}>
          Nova família
        </Key>
      </div>
    );

  return (
    <Face className="meter">
      <Label>{primeira ? "Criar a família" : "Nova família"}</Label>
      <div className="sub">
        O nome é só para você reconhecer na lista. Quem cria entra como admin e é quem pode
        adicionar e tirar gente.
      </div>
      <Field label="Nome da família">
        <input
          value={nome}
          autoFocus
          placeholder="Ex.: Casa, Seze"
          onChange={(e) => setNome(e.target.value)}
        />
      </Field>
      {erro && <ErrorBanner message={erro} />}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {!primeira && (
          <Key style={ALVO} onClick={() => setAberto(false)}>
            Cancelar
          </Key>
        )}
        <Key primary style={ALVO} disabled={!nome.trim() || criar.isPending} onClick={enviar}>
          {criar.isPending ? "Criando…" : "Criar família"}
        </Key>
      </div>
    </Face>
  );
}

function BlocoFamilia({ familia, contas }: { familia: Family; contas: Account[] }) {
  const { user } = useSession();
  const qc = useQueryClient();
  const adicionar = useAddMember();
  const compartilhar = useShareAccount();
  const descompartilhar = useUnshareAccount();
  // `api.removeMember` não tem hook em queries.ts (arquivo fechado nesta tarefa): a mutação
  // mora aqui e invalida a mesma chave que as outras escritas de família.
  const remover = useMutation({
    mutationFn: (userId: string) => api.removeMember(familia.id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.families }),
  });

  const [email, setEmail] = useState("");
  const [erroMembro, setErroMembro] = useState<string | null>(null);
  const [erroConta, setErroConta] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const membros = familia.members ?? [];
  const minhas = contas.filter((a) => a.user_id === user?.id);
  const dosOutros = contas.filter(
    (a) => a.user_id !== user?.id && (a.shares ?? []).some((s) => s.family_id === familia.id),
  );

  const estado = (a: Account): Estado =>
    (a.shares ?? []).find((s) => s.family_id === familia.id)?.visibility ?? "none";

  const mudar = async (a: Account, alvo: Estado) => {
    if (alvo === estado(a)) return;
    setErroConta(null);
    try {
      if (alvo === "none")
        await descompartilhar.mutateAsync({ familyId: familia.id, accountId: a.id });
      else
        await compartilhar.mutateAsync({
          familyId: familia.id,
          accountId: a.id,
          visibility: alvo,
        });
    } catch (e) {
      setErroConta((e as Error).message);
    }
  };

  const convidar = async () => {
    setErroMembro(null);
    try {
      await adicionar.mutateAsync({ familyId: familia.id, email: email.trim() });
      setEmail("");
    } catch (e) {
      setErroMembro((e as Error).message);
    }
  };

  const tirar = async (userId: string) => {
    setErroMembro(null);
    try {
      await remover.mutateAsync(userId);
      setConfirmando(null);
    } catch (e) {
      setErroMembro((e as Error).message);
    }
  };

  const mexendo = compartilhar.isPending || descompartilhar.isPending;

  return (
    <>
      <Face className="log">
        <div className="sec">
          <Label>{familia.name}</Label>
          <Label dim>
            {membros.length} {membros.length === 1 ? "pessoa" : "pessoas"}
          </Label>
        </div>
        {membros.length === 0 && <Empty title="Sem membros" />}
        {membros.map((m) => {
          const eu = m.user_id === user?.id;
          return (
            <div className="line" key={m.user_id}>
              <div className="d">
                <b>{iniciais(m.name)}</b>
              </div>
              <div className="what">
                <div className="t">
                  <span className="desc">{m.name}</span>
                  {eu && <span className="tag">Você</span>}
                  {m.role === "admin" && <span className="tag">Admin</span>}
                </div>
                <small>{m.email}</small>
              </div>
              <div style={{ display: "flex", gap: 6, paddingRight: 14 }}>
                {confirmando === m.user_id ? (
                  <>
                    <Key style={ALVO} onClick={() => setConfirmando(null)}>
                      Não
                    </Key>
                    <Key danger style={ALVO} disabled={remover.isPending} onClick={() => tirar(m.user_id)}>
                      {remover.isPending ? "Tirando…" : eu ? "Sair" : "Tirar"}
                    </Key>
                  </>
                ) : (
                  <Key
                    icon="trash"
                    danger
                    style={ALVO}
                    title={eu ? "Sair desta família" : `Tirar ${m.name} da família`}
                    onClick={() => setConfirmando(m.user_id)}
                  />
                )}
              </div>
            </div>
          );
        })}
        {erroMembro && <ErrorBanner message={erroMembro} />}
      </Face>

      <Face className="meter">
        <Label>Adicionar quem divide as contas</Label>
        <div className="sub">
          Digite o e-mail com que a pessoa entra no Fluxo. Quem sai da família deixa de ver as
          contas compartilhadas; nada é apagado.
        </div>
        <Field label="E-mail">
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            value={email}
            placeholder="pessoa@exemplo.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="rule">
          O Fluxo é fechado: não existe cadastro aberto nem convite por e-mail. A pessoa precisa já
          ter conta aqui, senão o servidor responde “Usuário não encontrado”.
        </div>
        <div style={{ marginTop: 8 }}>
          <Key primary style={ALVO} disabled={!email.trim() || adicionar.isPending} onClick={convidar}>
            {adicionar.isPending ? "Adicionando…" : "Adicionar"}
          </Key>
        </div>
      </Face>

      <Face className="meter">
        <Label>O que entra na visão da família</Label>
        <div className="sub">
          “Só o saldo” mostra quanto tem na conta, sem a lista de lançamentos; “tudo” mostra os dois.
        </div>
        {minhas.length === 0 && <Empty title="Você não tem conta nenhuma" />}
        {minhas.map((a) => (
          <FieldGroup key={a.id} label={a.name}>
            <div className="chips">
              {ESTADOS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="chip"
                  style={ALVO}
                  disabled={mexendo}
                  aria-pressed={estado(a) === o.id}
                  onClick={() => mudar(a, o.id)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </FieldGroup>
        ))}
        <div className="rule" style={{ marginTop: 6 }}>
          “Só o saldo” fecha o extrato de verdade: a outra pessoa vê a conta e quanto tem nela,
          e não vê um lançamento sequer. Quem decide é o banco, não a tela.
        </div>
        {erroConta && <ErrorBanner message={erroConta} />}
      </Face>

      {dosOutros.length > 0 && (
        <Face className="log">
          <div className="sec">
            <Label>Compartilhadas com você</Label>
            <Label dim>só o dono muda</Label>
          </div>
          {dosOutros.map((a) => (
            <div className="line" key={a.id}>
              <div className="d">
                <b>{iniciais(a.owner_name ?? "?")}</b>
              </div>
              <div className="what">
                <div className="t">
                  <span className="desc">{a.name}</span>
                </div>
                <small>
                  de {a.owner_name ?? "outro membro"} · {rotulo(estado(a))}
                </small>
              </div>
              <div className="amt">{money(a.balance)}</div>
            </div>
          ))}
        </Face>
      )}
    </>
  );
}
